import React, { createContext, useEffect, useState, useCallback } from "react";
import { colors, typography } from "./tokens";

export type ThemeMode = "light" | "dark";
interface ThemeCtx {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
  collapsed: boolean;
  setCollapsed: any;
  tabdata: any;
  setTabdata: any;
  radioval: any;
  setRadioval: any;
  scrnaseqdata: any;
  setScrnaseqdata: any;
  loading: boolean;
  setLoading: any;
}

const ThemeContext = createContext<ThemeCtx | undefined>(undefined);
export { ThemeContext }; // Export for useTheme hook
const STORAGE_KEY = "app-theme-mode";

function getSystemPref(): ThemeMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export const ThemeProvider: React.FC<
  React.PropsWithChildren<{ initialMode?: ThemeMode }>
> = ({ children, initialMode }) => {
  const [mode, setModeState] = useState<ThemeMode>(
    initialMode ||
      (localStorage.getItem(STORAGE_KEY) as ThemeMode) ||
      getSystemPref()
  );
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [radioval, setRadioval] = useState("");
  const [tabdata, setTabdata] = useState({
    geneinfo: { data: [], message: "" },
    tissueexpression: { data: [{}], message: "" },
    rnaseqdatasets: { data: [], message: "" },
    diseaseassociationscores: {
      data: { zscore: [], disease: [] },
      message: "",
    },
    diseaseassociationsknown: {
      data: {
        xval: [""],
        zval: [[0]],
      },
      message: "",
    },
    genetbl: {
      data: { headers: [{}], rows: [] },
      message: "",
    },
  });
  const [scrnaseqdata, setScrnaseqdata] = useState<any>({
    name: "",
    xvalues: [],
    yvalues: [],
    zvalues: [],
    description: "",
  });

  const apply = useCallback((m: ThemeMode) => {
    const palette = colors[m];
    const root = document.documentElement;
    root.dataset.theme = m;
    if (m === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    root.style.setProperty("--color-background", palette.background);
    root.style.setProperty("--color-surface", palette.surface);
    root.style.setProperty("--color-surface-alt", palette.surfaceAlt);
    root.style.setProperty("--color-text", palette.text);
    root.style.setProperty("--color-text-dim", palette.textDim);
    root.style.setProperty("--color-primary", palette.primary);
    root.style.setProperty("--color-primary-hover", palette.primaryHover);
    root.style.setProperty("--color-danger", palette.danger);
    root.style.setProperty("--color-border", palette.border);
    root.style.setProperty("--font-family", typography.fontFamily);
  }, []);

  const setMode = (m: ThemeMode) => {
    setModeState(m);
    localStorage.setItem(STORAGE_KEY, m);
  };

  const toggle = () => setMode(mode === "light" ? "dark" : "light");

  useEffect(() => {
    apply(mode);
  }, [mode, apply]);

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        toggle,
        collapsed,
        setCollapsed,
        tabdata,
        setTabdata,
        radioval,
        setRadioval,
        scrnaseqdata,
        setScrnaseqdata,
        loading,
        setLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
